import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  NIGERIAN_STATES,
  DEFAULT_SHIPPING_SETTINGS,
  getShippingSettings,
  saveShippingSettings,
  getPickupLocations,
  addPickupLocation,
  updatePickupLocation,
  deletePickupLocation,
} from "../utils/shipping";
import { formatPrice } from "../utils/format";
import {
  MapPin,
  Truck,
  Plus,
  Trash2,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Clock,
  Phone,
  Search,
  Package,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

// Sample starter pickup locations for quick one-click setup if admin wants
const SAMPLE_PICKUP_LOCATIONS = [
  {
    name: "Afriport Hub - Ikeja Central",
    state: "Lagos",
    city: "Ikeja",
    address: "Plot 12, Commercial Avenue, Ikeja, Lagos",
    phone: "08091234567",
    operatingHours: "Mon - Sat: 8:30 AM - 6:00 PM",
    notes: "Located near Computer Village roundabout. Present Order ID & valid ID.",
    active: true,
  },
  {
    name: "Afriport Hub - Lekki Phase 1",
    state: "Lagos",
    city: "Lekki",
    address: "Block 4, Admiralty Way, Lekki Phase 1, Lagos",
    phone: "08091234568",
    operatingHours: "Mon - Sat: 9:00 AM - 6:30 PM",
    notes: "Ample parking available. Packages held for up to 7 days.",
    active: true,
  },
  {
    name: "Afriport Hub - Abuja Central (Wuse 2)",
    state: "FCT",
    city: "Abuja",
    address: "No. 24 Aminu Kano Crescent, Wuse 2, Abuja",
    phone: "08091234569",
    operatingHours: "Mon - Sat: 9:00 AM - 6:00 PM",
    notes: "Opposite AP Plaza. Present Order Confirmation SMS or Email.",
    active: true,
  },
  {
    name: "Afriport Hub - Port Harcourt GRA",
    state: "Rivers",
    city: "Port Harcourt",
    address: "18 Tombia Street, GRA Phase 2, Port Harcourt",
    phone: "08091234570",
    operatingHours: "Mon - Fri: 9:00 AM - 5:30 PM, Sat: 10:00 AM - 4:00 PM",
    notes: "Fast collection center. Call contact number on arrival.",
    active: true,
  },
  {
    name: "Afriport Hub - Ibadan Bodija",
    state: "Oyo",
    city: "Ibadan",
    address: "5 Secretariat Road, Bodija, Ibadan",
    phone: "08091234571",
    operatingHours: "Mon - Sat: 9:00 AM - 5:00 PM",
    notes: "Beside Bodija Market entrance. Free parking available.",
    active: true,
  },
  {
    name: "Afriport Hub - Kano City Center",
    state: "Kano",
    city: "Kano",
    address: "14 Bompai Road, Fagge, Kano",
    phone: "08091234572",
    operatingHours: "Mon - Sat: 9:00 AM - 5:30 PM",
    notes: "Central hub for Kano and neighboring zones.",
    active: true,
  },
];

export default function AdminShippingPage() {
  const [activeTab, setActiveTab] = useState("locations"); // "locations" | "rates" | "products"
  const [settings, setSettings] = useState(DEFAULT_SHIPPING_SETTINGS);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Filter & Search states
  const [selectedStateFilter, setSelectedStateFilter] = useState("all");
  const [searchLocation, setSearchLocation] = useState("");
  const [rateSearch, setRateSearch] = useState("");

  // Location Form Modal State
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: "",
    state: "Lagos",
    city: "",
    address: "",
    phone: "",
    operatingHours: "Mon - Sat: 9:00 AM - 6:00 PM",
    notes: "",
    active: true,
  });

  // State Rates Local State
  const [stateRates, setStateRates] = useState({});

  // Products with custom shipping rules
  const [customProducts, setCustomProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load all data
  async function loadData() {
    setLoading(true);
    try {
      const [fetchedSettings, fetchedLocations] = await Promise.all([
        getShippingSettings(),
        getPickupLocations(null, false), // fetch all including inactive
      ]);
      setSettings(fetchedSettings);
      setStateRates(fetchedSettings.stateRates || {});
      setLocations(fetchedLocations);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load shipping data");
    } finally {
      setLoading(false);
    }
  }

  // Load products with custom shipping rules
  async function loadCustomProducts() {
    setLoadingProducts(true);
    try {
      const snap = await getDocs(collection(db, "products"));
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        if (
          data.shippingType === "free" ||
          data.shippingType === "fixed" ||
          data.shippingType === "surcharge" ||
          data.shippingSurcharge > 0 ||
          data.shippingFee > 0
        ) {
          list.push({ id: d.id, ...data });
        }
      });
      setCustomProducts(list);
    } catch (err) {
      console.warn("Could not load custom products:", err);
    } finally {
      setLoadingProducts(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === "products") {
      loadCustomProducts();
    }
  }, [activeTab]);

  // Open modal to add a new location
  function openAddLocationModal(preselectedState = "Lagos") {
    setEditingLocation(null);
    setLocationForm({
      name: "",
      state: preselectedState || "Lagos",
      city: "",
      address: "",
      phone: "",
      operatingHours: "Mon - Sat: 9:00 AM - 6:00 PM",
      notes: "",
      active: true,
    });
    setShowLocationModal(true);
  }

  // Open modal to edit existing location
  function openEditLocationModal(loc) {
    setEditingLocation(loc);
    setLocationForm({
      name: loc.name || "",
      state: loc.state || "Lagos",
      city: loc.city || "",
      address: loc.address || "",
      phone: loc.phone || "",
      operatingHours: loc.operatingHours || "Mon - Sat: 9:00 AM - 6:00 PM",
      notes: loc.notes || "",
      active: loc.active !== false,
    });
    setShowLocationModal(true);
  }

  // Handle Location Form Submission
  async function handleSaveLocation(e) {
    e.preventDefault();
    if (!locationForm.name.trim() || !locationForm.address.trim() || !locationForm.city.trim()) {
      return toast.error("Please fill in location name, city, and full address");
    }

    try {
      if (editingLocation) {
        await updatePickupLocation(editingLocation.id, locationForm);
        toast.success(`Updated ${locationForm.name}!`);
      } else {
        await addPickupLocation(locationForm);
        toast.success(`Added pickup location in ${locationForm.state}!`);
      }
      setShowLocationModal(false);
      const updated = await getPickupLocations(null, false);
      setLocations(updated);
    } catch (err) {
      toast.error("Failed to save location: " + err.message);
    }
  }

  // Toggle active/inactive
  async function handleToggleActive(loc) {
    try {
      await updatePickupLocation(loc.id, { active: !loc.active });
      setLocations((prev) =>
        prev.map((item) => (item.id === loc.id ? { ...item, active: !loc.active } : item))
      );
      toast.success(`${loc.name} is now ${!loc.active ? "Active" : "Inactive"}`);
    } catch (err) {
      toast.error("Failed to update status");
    }
  }

  // Delete location
  async function handleDeleteLocation(loc) {
    if (!window.confirm(`Are you sure you want to remove "${loc.name}" (${loc.state})?`)) return;
    try {
      await deletePickupLocation(loc.id);
      setLocations((prev) => prev.filter((item) => item.id !== loc.id));
      toast.success("Pickup location deleted");
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    }
  }

  // Populate sample starter hubs
  async function handleAddSampleHubs() {
    if (!window.confirm("Add 6 popular starter pickup hubs (Lagos, Abuja, Rivers, Oyo, Kano)?"))
      return;
    try {
      setLoading(true);
      for (const sample of SAMPLE_PICKUP_LOCATIONS) {
        await addPickupLocation(sample);
      }
      toast.success("Sample pickup hubs added successfully!");
      const updated = await getPickupLocations(null, false);
      setLocations(updated);
    } catch (err) {
      toast.error("Failed to add sample hubs: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  // Save Global Delivery Settings
  async function handleSaveGlobalSettings(e) {
    e?.preventDefault();
    setSavingSettings(true);
    try {
      const updated = {
        ...settings,
        defaultDeliveryFee: Number(settings.defaultDeliveryFee) || 3000,
        freeShippingThreshold: Number(settings.freeShippingThreshold) || 50000,
        enableFreeShipping: Boolean(settings.enableFreeShipping),
        pickupFee: Number(settings.pickupFee) || 0,
        deliveryDays: Number(settings.deliveryDays) || 7,
        pickupDays: Number(settings.pickupDays) || 5,
        stateRates,
      };
      await saveShippingSettings(updated);
      setSettings(updated);
      toast.success("Delivery & shipping rates saved!");
    } catch (err) {
      toast.error("Failed to save settings: " + err.message);
    } finally {
      setSavingSettings(false);
    }
  }

  // Handle single state rate change
  function handleStateRateChange(stateName, val) {
    const num = val === "" ? "" : Number(val);
    setStateRates((prev) => ({
      ...prev,
      [stateName]: num,
    }));
  }

  // Reset a state to use default base fee
  function resetStateToDefault(stateName) {
    setStateRates((prev) => {
      const copy = { ...prev };
      delete copy[stateName];
      return copy;
    });
    toast.success(`${stateName} reset to default base delivery fee`);
  }

  // Filtered pickup locations
  const filteredLocations = locations.filter((loc) => {
    const matchesState = selectedStateFilter === "all" || loc.state === selectedStateFilter;
    const q = searchLocation.toLowerCase();
    const matchesSearch =
      !q ||
      loc.name?.toLowerCase().includes(q) ||
      loc.city?.toLowerCase().includes(q) ||
      loc.address?.toLowerCase().includes(q) ||
      loc.state?.toLowerCase().includes(q);
    return matchesState && matchesSearch;
  });

  // Count active locations by state
  const locationsCountByState = locations.reduce((acc, loc) => {
    if (loc.active) {
      acc[loc.state] = (acc[loc.state] || 0) + 1;
    }
    return acc;
  }, {});

  // Filtered states for rates table
  const filteredStates = NIGERIAN_STATES.filter((s) =>
    s.toLowerCase().includes(rateSearch.toLowerCase())
  );

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 32,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "rgba(232, 71, 26, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--brand)",
              }}
            >
              <Truck size={22} />
            </span>
            <h1
              style={{
                fontFamily: "var(--font-head)",
                fontSize: 28,
                fontWeight: 800,
                color: "var(--text)",
                margin: 0,
              }}
            >
              Shipping & Pickup Management
            </h1>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 6, maxWidth: 750 }}>
            Manage station pickup locations per state, configure state delivery fees, and maintain
            the <strong>guaranteed 7-day delivery timeline</strong> for customer orders.
          </p>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          {activeTab === "locations" && (
            <button
              onClick={() =>
                openAddLocationModal(selectedStateFilter === "all" ? "Lagos" : selectedStateFilter)
              }
              className="btn btn-primary"
              style={{ padding: "12px 20px", fontSize: 14 }}
            >
              <Plus size={18} /> Add Pickup Location
            </button>
          )}

          {activeTab === "rates" && (
            <button
              onClick={handleSaveGlobalSettings}
              disabled={savingSettings}
              className="btn btn-primary"
              style={{ padding: "12px 24px", fontSize: 14 }}
            >
              {savingSettings ? "Saving…" : "Save All Delivery Rates"}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 28,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 2,
        }}
      >
        <button
          onClick={() => setActiveTab("locations")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 20px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: activeTab === "locations" ? 700 : 500,
            color: activeTab === "locations" ? "var(--brand)" : "var(--text-muted)",
            borderBottom:
              activeTab === "locations" ? "3px solid var(--brand)" : "3px solid transparent",
            transition: "all 0.2s",
          }}
        >
          <MapPin size={18} />
          <span>Pickup Locations ({locations.length})</span>
        </button>

        <button
          onClick={() => setActiveTab("rates")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 20px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: activeTab === "rates" ? 700 : 500,
            color: activeTab === "rates" ? "var(--brand)" : "var(--text-muted)",
            borderBottom: activeTab === "rates" ? "3px solid var(--brand)" : "3px solid transparent",
            transition: "all 0.2s",
          }}
        >
          <Truck size={18} />
          <span>State Delivery Rates & Policies</span>
        </button>

        <button
          onClick={() => setActiveTab("products")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 20px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: activeTab === "products" ? 700 : 500,
            color: activeTab === "products" ? "var(--brand)" : "var(--text-muted)",
            borderBottom:
              activeTab === "products" ? "3px solid var(--brand)" : "3px solid transparent",
            transition: "all 0.2s",
          }}
        >
          <Package size={18} />
          <span>Product Shipping Rules</span>
        </button>
      </div>

      {/* ── TAB 1: PICKUP LOCATIONS ────────────────────────────────────── */}
      {activeTab === "locations" && (
        <div>
          {/* Quick Notice Banner */}
          <div
            style={{
              padding: "16px 20px",
              background: "#F8FAFC",
              border: "1px solid var(--border)",
              borderRadius: 12,
              marginBottom: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#DCFCE7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--success)",
                }}
              >
                <Clock size={20} />
              </div>
              <div>
                <p style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "var(--text)" }}>
                  Pickup & 7-Day Fulfillment Flow
                </p>
                <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "2px 0 0" }}>
                  Customers selecting <strong>Pickup Station</strong> at checkout choose from the
                  stations available for their state. Orders are delivered to the station within 7
                  days of payment.
                </p>
              </div>
            </div>

            {locations.length === 0 && (
              <button
                onClick={handleAddSampleHubs}
                className="btn btn-secondary btn-sm"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <Sparkles size={14} color="var(--brand)" /> Add 6 Sample Starter Hubs
              </button>
            )}
          </div>

          {/* Filter Bar */}
          <div
            className="card"
            style={{
              padding: "16px 20px",
              marginBottom: 24,
              background: "#fff",
              border: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 240, position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              />
              <input
                className="form-input"
                placeholder="Search pickup hub, address, or city..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                style={{ paddingLeft: 40, fontSize: 14, borderRadius: 8 }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text-muted)",
                  whiteSpace: "nowrap",
                }}
              >
                State:
              </label>
              <select
                className="form-select"
                value={selectedStateFilter}
                onChange={(e) => setSelectedStateFilter(e.target.value)}
                style={{ minWidth: 160, fontSize: 14, borderRadius: 8 }}
              >
                <option value="all">All States ({locations.length})</option>
                {NIGERIAN_STATES.map((s) => {
                  const count = locationsCountByState[s] || 0;
                  return (
                    <option key={s} value={s}>
                      {s} {count > 0 ? `(${count})` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Locations Grid */}
          {loading ? (
            <div style={{ padding: 60, textAlign: "center" }}>
              <div className="loader">
                <div className="spinner" />
              </div>
            </div>
          ) : filteredLocations.length === 0 ? (
            <div
              className="card"
              style={{
                padding: 60,
                textAlign: "center",
                background: "#fff",
                border: "1px dashed var(--border)",
                borderRadius: 16,
              }}
            >
              <MapPin size={48} color="var(--brand)" style={{ margin: "0 auto 16px", opacity: 0.6 }} />
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 20, marginBottom: 8 }}>
                No pickup locations found
              </h3>
              <p style={{ color: "var(--text-muted)", fontSize: 14, maxWidth: 500, margin: "0 auto 20px" }}>
                {selectedStateFilter !== "all"
                  ? `There are currently no pickup locations in ${selectedStateFilter}. Customers selecting ${selectedStateFilter} will see a notification to choose door delivery or another hub.`
                  : "You haven't added any pickup locations yet. Add locations so customers can pick up their orders."}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button
                  onClick={() =>
                    openAddLocationModal(
                      selectedStateFilter === "all" ? "Lagos" : selectedStateFilter
                    )
                  }
                  className="btn btn-primary"
                >
                  <Plus size={16} /> Add Location in{" "}
                  {selectedStateFilter === "all" ? "Lagos" : selectedStateFilter}
                </button>
                {locations.length === 0 && (
                  <button onClick={handleAddSampleHubs} className="btn btn-secondary">
                    Load Starter Hubs
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
                gap: 20,
              }}
            >
              {filteredLocations.map((loc) => (
                <div
                  key={loc.id}
                  className="card"
                  style={{
                    padding: 24,
                    background: "#fff",
                    border: `1px solid ${loc.active ? "var(--border)" : "#FCA5A5"}`,
                    boxShadow: "var(--shadow-sm)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    opacity: loc.active ? 1 : 0.75,
                    transition: "all 0.2s",
                  }}
                >
                  <div>
                    {/* Card Header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                      }}
                    >
                      <span
                        className="badge badge-default"
                        style={{
                          background: "var(--brand-light)",
                          color: "var(--brand)",
                          fontWeight: 700,
                          fontSize: 12,
                          padding: "4px 10px",
                        }}
                      >
                        {loc.state} State · {loc.city}
                      </span>

                      <button
                        onClick={() => handleToggleActive(loc)}
                        title={loc.active ? "Click to disable" : "Click to activate"}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: loc.active ? "var(--success)" : "#94A3B8",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {loc.active ? (
                          <>
                            <ToggleRight size={26} color="var(--success)" /> Active
                          </>
                        ) : (
                          <>
                            <ToggleLeft size={26} color="#94A3B8" /> Inactive
                          </>
                        )}
                      </button>
                    </div>

                    <h3
                      style={{
                        fontFamily: "var(--font-head)",
                        fontSize: 17,
                        fontWeight: 700,
                        color: "var(--text)",
                        marginBottom: 8,
                      }}
                    >
                      {loc.name}
                    </h3>

                    {/* Address */}
                    <p
                      style={{
                        fontSize: 14,
                        color: "#475569",
                        lineHeight: 1.5,
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <MapPin
                        size={16}
                        color="var(--brand)"
                        style={{ flexShrink: 0, marginTop: 3 }}
                      />
                      <span>{loc.address}</span>
                    </p>

                    {/* Contact Phone & Hours */}
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--text-muted)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        background: "#F8FAFC",
                        padding: "10px 12px",
                        borderRadius: 8,
                        marginBottom: 16,
                      }}
                    >
                      {loc.phone && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Phone size={14} color="var(--text-muted)" />
                          <span>Phone: {loc.phone}</span>
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Clock size={14} color="var(--text-muted)" />
                        <span>Hours: {loc.operatingHours || "Mon - Sat: 9am - 6pm"}</span>
                      </div>
                    </div>

                    {loc.notes && (
                      <p
                        style={{
                          fontSize: 12,
                          color: "var(--text-muted)",
                          fontStyle: "italic",
                          marginBottom: 16,
                        }}
                      >
                        ℹ️ {loc.notes}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      borderTop: "1px solid var(--border)",
                      paddingTop: 14,
                      marginTop: 8,
                    }}
                  >
                    <button
                      onClick={() => openEditLocationModal(loc)}
                      className="btn btn-secondary btn-sm"
                      style={{ padding: "6px 12px", fontSize: 13 }}
                    >
                      <Edit2 size={13} /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteLocation(loc)}
                      style={{
                        background: "rgba(239, 68, 68, 0.08)",
                        color: "#DC2626",
                        border: "1px solid rgba(239, 68, 68, 0.2)",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: STATE DELIVERY RATES & SETTINGS ───────────────────── */}
      {activeTab === "rates" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {/* Global Settings Box */}
          <div
            className="card"
            style={{
              padding: 32,
              background: "#fff",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "rgba(232, 71, 26, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--brand)",
                }}
              >
                <Truck size={18} />
              </span>
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, margin: 0 }}>
                Global Delivery & Fulfillment Policies
              </h3>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                gap: 20,
              }}
            >
              {/* Default Delivery Fee */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Default Base Delivery Fee (₦) *
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={settings.defaultDeliveryFee}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, defaultDeliveryFee: e.target.value }))
                  }
                  placeholder="3000"
                  style={{ padding: "12px 16px", borderRadius: 8, fontSize: 15 }}
                />
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Applied to any state without a specific custom rate.
                </span>
              </div>

              {/* Free Shipping Threshold */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Free Delivery Threshold (₦)
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={settings.freeShippingThreshold}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, freeShippingThreshold: e.target.value }))
                  }
                  placeholder="50000"
                  style={{ padding: "12px 16px", borderRadius: 8, fontSize: 15 }}
                />
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Orders at or above this amount qualify for free delivery.
                </span>
              </div>

              {/* Default Pickup Fee */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Pickup Station Fee (₦)
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={settings.pickupFee}
                  onChange={(e) => setSettings((s) => ({ ...s, pickupFee: e.target.value }))}
                  placeholder="0"
                  style={{ padding: "12px 16px", borderRadius: 8, fontSize: 15 }}
                />
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Set to 0 for Free Station Pickup.
                </span>
              </div>

              {/* Guaranteed Delivery Window */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Guaranteed Delivery Window (Days) *
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  max={30}
                  value={settings.deliveryDays || 7}
                  onChange={(e) => setSettings((s) => ({ ...s, deliveryDays: e.target.value }))}
                  placeholder="7"
                  style={{ padding: "12px 16px", borderRadius: 8, fontSize: 15 }}
                />
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Standard guarantee: Within 7 days of payment.
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 24,
                paddingTop: 20,
                borderTop: "1px solid var(--border)",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  id="enableFreeShipping"
                  checked={settings.enableFreeShipping !== false}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, enableFreeShipping: e.target.checked }))
                  }
                  style={{ width: 18, height: 18, accentColor: "var(--brand)", cursor: "pointer" }}
                />
                <label
                  htmlFor="enableFreeShipping"
                  style={{ fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >
                  Enable automatic Free Delivery for qualifying carts (above{" "}
                  {formatPrice(settings.freeShippingThreshold || 50000)})
                </label>
              </div>

              <button
                type="button"
                onClick={handleSaveGlobalSettings}
                disabled={savingSettings}
                className="btn btn-primary"
                style={{ padding: "10px 24px" }}
              >
                {savingSettings ? "Saving…" : "Save Global Settings"}
              </button>
            </div>
          </div>

          {/* State Rates Table Card */}
          <div
            className="card"
            style={{
              padding: 32,
              background: "#fff",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, margin: 0 }}>
                  Delivery Fee by Nigerian State / Zone
                </h3>
                <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                  Set custom delivery fees for specific states. Unset states will automatically use
                  the default rate of {formatPrice(settings.defaultDeliveryFee || 3000)}.
                </p>
              </div>

              <div style={{ position: "relative", width: 260 }}>
                <Search
                  size={15}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-muted)",
                  }}
                />
                <input
                  className="form-input"
                  placeholder="Filter states..."
                  value={rateSearch}
                  onChange={(e) => setRateSearch(e.target.value)}
                  style={{ paddingLeft: 34, fontSize: 13, borderRadius: 8 }}
                />
              </div>
            </div>

            <div
              style={{
                overflowX: "auto",
                border: "1px solid var(--border)",
                borderRadius: 12,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                      }}
                    >
                      State / Zone
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                      }}
                    >
                      Delivery Fee (₦)
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "right",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStates.map((state) => {
                    const isCustom = stateRates[state] !== undefined && stateRates[state] !== "";
                    const currentFee = isCustom ? stateRates[state] : settings.defaultDeliveryFee || 3000;
                    return (
                      <tr
                        key={state}
                        style={{
                          borderBottom: "1px solid var(--border)",
                          background: isCustom ? "rgba(232, 71, 26, 0.02)" : "transparent",
                        }}
                      >
                        <td style={{ padding: "14px 20px", fontWeight: 600 }}>{state}</td>
                        <td style={{ padding: "14px 20px" }}>
                          {isCustom ? (
                            <span
                              className="badge badge-info"
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                background: "rgba(59, 130, 246, 0.1)",
                                color: "#2563EB",
                              }}
                            >
                              Custom Rate
                            </span>
                          ) : (
                            <span
                              className="badge badge-default"
                              style={{ fontSize: 11, color: "var(--text-muted)" }}
                            >
                              Default Rate
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "14px 20px", maxWidth: 220 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: "var(--text-muted)", fontSize: 14 }}>₦</span>
                            <input
                              className="form-input"
                              type="number"
                              min={0}
                              placeholder={String(settings.defaultDeliveryFee || 3000)}
                              value={stateRates[state] ?? ""}
                              onChange={(e) => handleStateRateChange(state, e.target.value)}
                              style={{
                                width: 140,
                                padding: "6px 10px",
                                borderRadius: 6,
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right" }}>
                          {isCustom ? (
                            <button
                              type="button"
                              onClick={() => resetStateToDefault(state)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "var(--text-muted)",
                                fontSize: 12,
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Reset to default
                            </button>
                          ) : (
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                              {formatPrice(currentFee)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 24,
                paddingTop: 16,
                borderTop: "1px solid var(--border)",
              }}
            >
              <button
                type="button"
                onClick={handleSaveGlobalSettings}
                disabled={savingSettings}
                className="btn btn-primary btn-lg"
                style={{ padding: "12px 32px" }}
              >
                {savingSettings ? "Saving…" : "Save All Delivery Rates"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: PRODUCT SHIPPING RULES ────────────────────────────── */}
      {activeTab === "products" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Guide Card */}
          <div
            className="card"
            style={{
              padding: 28,
              background: "#fff",
              border: "1px solid var(--border)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <Package size={20} color="var(--brand)" />
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, margin: 0 }}>
                How Product-Level Shipping Rules Work
              </h3>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
              Afriport calculates final shipping and delivery fee based on both the{" "}
              <strong>delivery location</strong> and individual <strong>product attributes</strong>:
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                gap: 16,
              }}
            >
              <div
                style={{
                  padding: 16,
                  borderRadius: 10,
                  background: "#F8FAFC",
                  border: "1px solid var(--border)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--brand)", marginBottom: 4 }}>
                  1. Standard Shipping
                </div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5, margin: 0 }}>
                  Uses the customer's selected State delivery fee (or free delivery if cart qualifies
                  for the free shipping threshold).
                </p>
              </div>

              <div
                style={{
                  padding: 16,
                  borderRadius: 10,
                  background: "#F8FAFC",
                  border: "1px solid var(--border)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--success)", marginBottom: 4 }}>
                  2. Free Shipping
                </div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5, margin: 0 }}>
                  Ships free nationwide, regardless of order total or customer state. Perfect for
                  promotions or digital/compact goods.
                </p>
              </div>

              <div
                style={{
                  padding: 16,
                  borderRadius: 10,
                  background: "#F8FAFC",
                  border: "1px solid var(--border)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "#D97706", marginBottom: 4 }}>
                  3. Heavy / Bulky Item Surcharge
                </div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5, margin: 0 }}>
                  Adds a designated freight surcharge (e.g. ₦5,000 for tubular batteries, 5kVA
                  inverters, solar panel pallets) to cover freight handling.
                </p>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <Link to="/admin/products" className="btn btn-secondary btn-sm">
                View & Edit Products Catalog →
              </Link>
            </div>
          </div>

          {/* List of products with custom shipping rules */}
          <div
            className="card"
            style={{
              padding: 28,
              background: "#fff",
              border: "1px solid var(--border)",
            }}
          >
            <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              Products with Custom Shipping Rules ({customProducts.length})
            </h3>

            {loadingProducts ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <div className="loader">
                  <div className="spinner" />
                </div>
              </div>
            ) : customProducts.length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
                No products currently have custom shipping rules configured. All active products are
                using standard location-based shipping fees. You can set custom rules when adding or
                editing a product in the admin panel.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Product</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Category</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Rule</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Fee / Surcharge</th>
                      <th style={{ padding: "12px 16px", textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customProducts.map((p) => (
                      <tr key={p.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "12px 16px", fontWeight: 600 }}>{p.name}</td>
                        <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{p.category}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span
                            className={`badge ${
                              p.shippingType === "free"
                                ? "badge-success"
                                : p.shippingType === "surcharge"
                                ? "badge-warning"
                                : "badge-info"
                            }`}
                            style={{ textTransform: "capitalize" }}
                          >
                            {p.shippingType === "free"
                              ? "Free Shipping"
                              : p.shippingType === "surcharge"
                              ? "Heavy Surcharge"
                              : "Fixed Fee"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                          {p.shippingType === "free"
                            ? "₦0 (Free)"
                            : p.shippingType === "surcharge"
                            ? `+${formatPrice(p.shippingSurcharge || 0)}`
                            : formatPrice(p.shippingFee || 0)}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <Link
                            to={`/admin/products/edit/${p.id}`}
                            style={{ color: "var(--brand)", fontWeight: 600, textDecoration: "none" }}
                          >
                            Edit Product →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: ADD / EDIT PICKUP LOCATION ───────────────────────── */}
      {showLocationModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 9999,
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: 600,
              width: "100%",
              background: "#fff",
              padding: 32,
              borderRadius: 16,
              boxShadow: "var(--shadow-lg)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 700, margin: 0 }}>
                {editingLocation ? "Edit Pickup Location" : "Add Pickup Location"}
              </h3>
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 22,
                  cursor: "pointer",
                  color: "var(--text-muted)",
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveLocation} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* State & City */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                    State *
                  </label>
                  <select
                    className="form-select"
                    required
                    value={locationForm.state}
                    onChange={(e) => setLocationForm((f) => ({ ...f, state: e.target.value }))}
                  >
                    {NIGERIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                    City / LGA *
                  </label>
                  <input
                    className="form-input"
                    required
                    placeholder="e.g. Ikeja, Wuse 2, Port Harcourt"
                    value={locationForm.city}
                    onChange={(e) => setLocationForm((f) => ({ ...f, city: e.target.value }))}
                  />
                </div>
              </div>

              {/* Station Name */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                  Pickup Station / Hub Name *
                </label>
                <input
                  className="form-input"
                  required
                  placeholder="e.g. Afriport Hub - Ikeja Central"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              {/* Full Address */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                  Full Street Address *
                </label>
                <textarea
                  className="form-input"
                  rows={2}
                  required
                  placeholder="Plot number, street name, nearest landmark..."
                  value={locationForm.address}
                  onChange={(e) => setLocationForm((f) => ({ ...f, address: e.target.value }))}
                />
              </div>

              {/* Contact Phone & Operating Hours */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                    Contact Phone
                  </label>
                  <input
                    className="form-input"
                    placeholder="08012345678"
                    value={locationForm.phone}
                    onChange={(e) => setLocationForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>

                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                    Operating Hours
                  </label>
                  <input
                    className="form-input"
                    placeholder="Mon - Sat: 9:00 AM - 6:00 PM"
                    value={locationForm.operatingHours}
                    onChange={(e) =>
                      setLocationForm((f) => ({ ...f, operatingHours: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* Special Instructions / Notes */}
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                  Pickup Instructions / Notes (Optional)
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. Present Order ID and valid ID upon arrival"
                  value={locationForm.notes}
                  onChange={(e) => setLocationForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>

              {/* Active Toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                <input
                  type="checkbox"
                  id="locActive"
                  checked={locationForm.active}
                  onChange={(e) => setLocationForm((f) => ({ ...f, active: e.target.checked }))}
                  style={{ width: 18, height: 18, accentColor: "var(--brand)", cursor: "pointer" }}
                />
                <label htmlFor="locActive" style={{ fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Active (visible to customers at checkout)
                </label>
              </div>

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: "1px solid var(--border)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ padding: "10px 24px" }}>
                  {editingLocation ? "Save Changes" : "Create Location"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
